import { Link } from 'react-router-dom';
import {
  Shield,
  Users,
  Building2,
  Trophy,
  Activity,
  Settings,
  UserPlus,
  Calendar,
  ArrowRight,
  Server,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useTotalUsers,
  useActiveOrganizations,
  useSystemHealth,
  useActiveJackpots,
  useRecentEvents,
  useMemberStatistics,
} from '../../api/extensions/useDashboardData';
import { DrawnOutWarningCard } from '../../components/dashboard/DrawnOutWarningCard';

function StatCard({
  title,
  value,
  icon: Icon,
  description,
  isLoading,
}: {
  title: string;
  value: string | number;
  icon: React.ElementType;
  description?: string;
  isLoading: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <p className="text-2xl font-bold">{value}</p>
            )}
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const quickActions = [
  { label: 'Manage Users', icon: Users, path: '/users', description: 'View and manage user accounts' },
  { label: 'Organizations', icon: Building2, path: '/organizations', description: 'Manage organizations' },
  { label: 'Bulk Entry', icon: UserPlus, path: '/admin/bulk-registration', description: 'Batch enter users' },
  {
    label: 'Member Verifications',
    icon: Shield,
    path: '/delegate/membership-verifications',
    description: 'Confirm or reject pending member claims',
  },
  { label: 'Settings', icon: Settings, path: '/settings', description: 'System configuration' },
];

export default function AdminDashboardPage() {
  const { data: totalUsers, isLoading: loadingUsers } = useTotalUsers();
  const { data: activeOrgs, isLoading: loadingOrgs } = useActiveOrganizations();
  const { data: systemHealth, isLoading: loadingHealth } = useSystemHealth();
  const { data: activeJackpots, isLoading: loadingJackpots } = useActiveJackpots();
  const { data: recentEvents, isLoading: loadingEvents } = useRecentEvents(30);
  const { data: memberStats, isLoading: loadingMembers } = useMemberStatistics();

  const uptimeHours = systemHealth?.uptime_seconds
    ? Math.floor(systemHealth.uptime_seconds / 3600)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <Shield className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            System overview and management
          </p>
        </div>
      </div>

      {/* Drawn-out cancellation reminder — self-hides when nothing pending */}
      <DrawnOutWarningCard />

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Users"
          value={totalUsers?.total ?? 0}
          icon={Users}
          isLoading={loadingUsers}
        />
        <StatCard
          title="Organizations"
          value={activeOrgs?.total ?? 0}
          icon={Building2}
          isLoading={loadingOrgs}
        />
        <StatCard
          title="Active Jackpots"
          value={activeJackpots?.count ?? 0}
          icon={Trophy}
          isLoading={loadingJackpots}
        />
        <StatCard
          title="Active Members"
          value={memberStats?.active_members ?? 0}
          icon={Activity}
          description={`of ${memberStats?.total_members ?? 0} total`}
          isLoading={loadingMembers}
        />
      </div>

      {/* Two-column layout */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {quickActions.map((action) => (
              <Link
                key={action.path}
                to={action.path}
                className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-accent"
              >
                <div className="flex items-center gap-3">
                  <action.icon className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{action.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {action.description}
                    </p>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            ))}
          </CardContent>
        </Card>

        {/* System Health */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">System Health</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingHealth ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Server className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Server Status</span>
                  </div>
                  <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                    Online
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Active Sessions</span>
                  <span className="text-sm font-medium">
                    {systemHealth?.active_sessions ?? 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Uptime</span>
                  <span className="text-sm font-medium">
                    {uptimeHours}h
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Events */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Recent Events (Last 30 Days)</CardTitle>
          <Link to="/rodeos">
            <Button variant="ghost" size="sm">
              View All <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {loadingEvents ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : recentEvents && recentEvents.length > 0 ? (
            <div className="space-y-2">
              {recentEvents.slice(0, 5).map((event) => (
                <Link
                  key={event.id}
                  to={`/events/${event.id}`}
                  className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-accent"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {event.event_name}
                    </span>
                  </div>
                  {event.start_date && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(event.start_date).toLocaleDateString()}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No recent events
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
